<template>
  <form class="contents" @submit.prevent="save">
    <tabs/>
    <toolbar/>
    <menu-bar class="flex justify-center" :hidden="!isDirty">
      <div class="w-card-content flex justify-end gap-x-7.5">
        <custom-button variant="outlined" @click="reset">
          {{ $t('reset') }}
        </custom-button>
        <custom-button variant="primary" type="submit" :busy="saving">
          {{ $t('save') }}
        </custom-button>
      </div>
    </menu-bar>
    <busy-area :busy="loadingSettings">
      <cards class="mb-20">
        <card :title="$t('pages.settings.email.title')">
          <section class="card-section">
            <FormInput
              v-model="internalSettings.sendgrid.sender.name"
              :error="errors?.sendgrid?.sender?.name"
              class="w-full"
              type="text"
              :label="$t('pages.settings.email.senderName')"
            />
            <FormInput
              v-model="internalSettings.sendgrid.sender.email"
              :error="errors?.sendgrid?.sender?.email"
              class="w-full"
              type="text"
              :label="$t('pages.settings.email.senderEmail')"
            />
            <FormInput
              v-model="internalSettings.sendgrid.host"
              :error="errors?.sendgrid?.host"
              class="w-full"
              type="text"
              :label="$t('pages.settings.email.host')"
            />
            <FormInput
              v-model="internalSettings.sendgrid.apiKey"
              :error="errors?.sendgrid?.apiKey"
              class="w-full"
              type="text"
              :label="$t('pages.settings.email.apiKey')"
            />
            <FormInput
              v-model="internalSettings.sendgrid.asmGroupId"
              :error="errors?.sendgrid?.asmGroupId"
              class="w-full"
              type="text"
              :label="$t('pages.settings.email.asmGroupId')"
            />
          </section>
        </card>
        <card :title="$t('pages.settings.sms.title')">
          <section class="card-section">
            <FormInput
              v-model="internalSettings.sms.sid"
              :error="errors?.sms?.sid"
              class="w-full"
              type="text"
              step="any"
              :label="$t('pages.settings.sms.sid')"
            />
            <FormInput
              v-model="internalSettings.sms.token"
              :error="errors?.sms?.token"
              class="w-full"
              type="text"
              step="any"
              :label="$t('pages.settings.sms.token')"
            />
            <FormInput
              v-model="internalSettings.sms.number"
              :error="errors?.sms?.number"
              class="w-full"
              type="text"
              step="any"
              :label="$t('pages.settings.sms.number')"
            />
          </section>
        </card>
        <card :title="$t('pages.settings.zendesk.title')">
          <section class="card-section">
            <FormInput
              v-model="internalSettings.zendesk.subdomain"
              :error="errors?.zendesk?.subdomain"
              class="w-full"
              type="text"
              :label="$t('pages.settings.zendesk.subdomain')"
            />
            <FormInput
              v-model="internalSettings.zendesk.username"
              :error="errors?.zendesk?.username"
              class="w-full"
              type="text"
              :label="$t('pages.settings.zendesk.username')"
            />
            <FormInput
              v-model="internalSettings.zendesk.token"
              :error="errors?.zendesk?.token"
              class="w-full"
              type="text"
              :label="$t('pages.settings.zendesk.token')"
            />
          </section>
        </card>
        <card :title="$t('pages.settings.pdf.title')">
          <section class="card-section">
            <FormInput
              v-model="internalSettings.pdfGenerator.route"
              :error="errors?.pdfGenerator?.route"
              class="w-full"
              type="text"
              :label="$t('pages.settings.pdf.route')"
            />
            <FormInput
              v-model="internalSettings.pdfGenerator.token"
              :error="errors?.pdfGenerator?.token"
              class="w-full"
              type="text"
              :label="$t('pages.settings.pdf.token')"
            />
          </section>
        </card>
        <card :title="$t('pages.settings.payoutRules.title')">
          <section class="card-section">
            <FormInput
              v-model="internalSettings.payoutRules.baseAmount"
              :error="errors?.payoutRules?.baseAmount"
              class="w-full"
              type="number"
              step="any"
              :label="$t('pages.settings.payoutRules.baseAmount')"
            />
            <FormInput
              v-model="internalSettings.payoutRules.kmPerWeek"
              :error="errors?.payoutRules?.kmPerWeek"
              class="w-full"
              type="number"
              step="any"
              :label="$t('pages.settings.payoutRules.kmPerWeek')"
            />
            <FormInput
              v-model="internalSettings.payoutRules.daysWithTourPerWeek"
              :error="errors?.payoutRules?.daysWithTourPerWeek"
              class="w-full"
              type="number"
              step="any"
              :label="$t('pages.settings.payoutRules.daysWithTourPerWeek')"
            />
          </section>
        </card>
        <card :title="$t('pages.settings.box.title')">
          <section class="card-section">
            <span v-if="errors?.box" class="text-sm text-red">
              {{ $t('validation.notEmpty') }}
            </span>
            <div v-for="version in boxVersions">
              <h3>{{ version.value }}</h3>
              <div class="flex gap-x-5 mt-5">
                <FormInput
                  v-model="internalSettings.box[version.value].price"
                  :error="errors?.box?.[version.value]?.price"
                  class="w-full"
                  type="number"
                  step="any"
                  :label="$t('pages.settings.box.totalPrice')"
                />
                <FormInput
                  v-model="internalSettings.box[version.value].depot"
                  :error="errors?.box?.[version.value]?.depot"
                  class="w-full"
                  type="number"
                  step="any"
                  :label="$t('pages.settings.box.depot')"
                />
              </div>
            </div>
          </section>
        </card>
        <card :title="$t('pages.settings.appVersions.title')">
          <section class="card-section">
            <FormInput
              v-model="internalSettings.appVersions.android.minVersion"
              :error="errors?.appVersions?.android.minVersion"
              class="w-full"
              :label="$t('pages.settings.appVersions.android.minVersion')"
            />
            <FormInput
              v-model="internalSettings.appVersions.android.desiredVersion"
              :error="errors?.appVersions?.android.desiredVersion"
              class="w-full"
              :label="$t('pages.settings.appVersions.android.desiredVersion')"
            />
            <FormInput
              v-model="internalSettings.appVersions.ios.minVersion"
              :error="errors?.appVersions?.ios.minVersion"
              class="w-full"
              :label="$t('pages.settings.appVersions.ios.minVersion')"
            />
            <FormInput
              v-model="internalSettings.appVersions.ios.desiredVersion"
              :error="errors?.appVersions?.ios.desiredVersion"
              class="w-full"
              :label="$t('pages.settings.appVersions.ios.desiredVersion')"
            />
          </section>
        </card>
        <card :title="$t('pages.settings.rulesLink.title')">
          <section class="card-section">
            <FormInput
              v-model="internalSettings.rulesLink.link"
              :error="errors?.appVersions?.link"
              class="w-full"
            />
          </section>
        </card>
        <card :title="$t('pages.settings.advertisements.title')">
          <section class="card-section">
            <Switch v-model="internalSettings.advertisements.enabled" :error="errors?.advertisements.enabled" :label="$t('pages.settings.advertisements.enabled')" />
          </section>
        </card>
      </cards>
    </busy-area>
  </form>
</template>

<script lang="ts" setup>
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Cards from '@components/utils/Cards.vue';
import Card from '@components/utils/Card.vue';
import Switch from '@utils/inputs/Switch.vue';
import {useI18n} from 'vue-i18n';
import Tabs from '@/components/structure/navigation/Tabs.vue';
import MenuBar from '@/components/utils/MenuBar.vue';
import BusyArea from '@/components/utils/busy/BusyArea.vue';
import CustomButton from '@utils/CustomButton.vue';
import {boolean, number, object, string} from 'yup';
import {computed, ref, watch} from 'vue';
import FormInput from '@utils/inputs/Input.vue';
import {useFetchAction, useFetchQuery} from '@/hooks/fetch';
import {useNotify} from '@/components/utils/notifications';
import {useDirty, useValidation} from '@/hooks/form';
import type {HydraCollection, Setting} from '@/entities/Entities';
import {syncRef} from '@/hooks/state';
import {enumOptionsProvider} from "@utils/table/hooks/provider";

const {t} = useI18n();

const {
  data: settingsCollection,
  loading: loadingSettings,
  refetch
} = useFetchQuery<HydraCollection<Setting>>('/settings');

const originalSettings = computed(() => settingsCollection.value?.['hydra:member'] ?? []);

const [exec, {loading: saving}] = useFetchAction();

const boxVersions = ref();
const loadBoxVersions = async () => {
  boxVersions.value = await enumOptionsProvider('/enums/BoxType')();
};
loadBoxVersions();

const getSettingsCopy = (currentSettings: Setting[]) => {
  const out: { [T: string]: object } = {
    sendgrid: {
      host: undefined,
      apiKey: undefined,
      sender: {
        name: undefined,
        email: undefined,
      },
      asmGroupId: undefined,
    },
    sms: {
      sid: undefined,
      token: undefined,
      number: undefined,
    },
    zendesk: {
      subdomain: undefined,
      username: undefined,
      token: undefined,
    },
    pdfGenerator: {
      route: undefined,
      token: undefined,
    },
    payoutRules: {
      baseAmount: undefined,
      kmPerWeek: undefined,
      daysWithTourPerWeek: undefined,
    },
    box: {},
    appVersions: {
      android: {
        minVersion: undefined,
        desiredVersion: undefined,
      },
      ios: {
        minVersion: undefined,
        desiredVersion: undefined,
      },
    },
    rulesLink: {
      link: undefined,
    },
    advertisements: {
      enabled: undefined,
    }
  };

  currentSettings.forEach((el) => {
    out[el.name] = JSON.parse(JSON.stringify(el.values));
  });
  return out;
};

const settingsRef = syncRef(originalSettings, getSettingsCopy);
const internalSettings = syncRef(originalSettings, getSettingsCopy);

const isDirty = useDirty(internalSettings, settingsRef);

const reset = () => {
  internalSettings.value = getSettingsCopy(originalSettings.value);
};

const notify = useNotify();

const {data: settings, errors, validatedFn} = useValidation(object({
  sendgrid: object({
    sender: object({
      name: string().required(t('validation.notEmpty')),
      email: string().email(t('validation.emailValid')).required(t('validation.notEmpty')),
    }),
    apiKey: string().required(t('validation.notEmpty')),
    host: string().required(t('validation.notEmpty')),
    asmGroupId: number().required(t('validation.notEmpty')),
  }),
  sms: object({
    sid: string().required(t('validation.notEmpty')),
    token: string().required(t('validation.notEmpty')),
    number: string().required(t('validation.notEmpty')),
  }),
  zendesk: object({
    subdomain: string().required(t('validation.notEmpty')),
    username: string().required(t('validation.notEmpty')),
    token: string().required(t('validation.notEmpty')),
  }),
  pdfGenerator: object({
    route: string().required(t('validation.notEmpty')),
    token: string().required(t('validation.notEmpty')),
  }),
  payoutRules: object({
    baseAmount: number().required(t('validation.notEmpty')),
    kmPerWeek: number().required(t('validation.notEmpty')),
    daysWithTourPerWeek: number().required(t('validation.notEmpty')),
  }),
  box: object().test('box', t('validation.notEmpty'), (value) => {
    const versions = boxVersions.value.map((el) => el.value);
    const keys = Object.keys(value);
    return versions.length === keys.length
      && versions.every((el) => keys.includes(el))
      && versions.every((el) => value[el]?.price !== undefined && value[el]?.depot !== undefined);
  }),
  appVersions: object({
    android: object({
      minVersion: string().required('validation.notEmpty'),
      desiredVersion: string().required('validation.notEmpty'),
    }),
    ios: object({
      minVersion: string().required('validation.notEmpty'),
      desiredVersion: string().required('validation.notEmpty'),
    }),
  }),
  rulesLink: object({
    link: string().required('validation.notEmpty'),
  }),
  advertisements: object({
    enabled: boolean().required('validation.notEmpty'),
  }),
}), internalSettings);

const save = validatedFn(async () => {
  try {
    await exec<Setting>('/settings', {
      method: 'PUT',
      body: {
        ...settings.value,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.settings.saveSuccess'),
    });

    await refetch();
  } catch (e) {
    console.error(e);
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  }
});
</script>

<style lang="scss" scoped>
form {
  --card-padding: #{px(60)};
}

.card-section {
  @apply grid grid-cols-1 gap-x-5 gap-y-7.5 w-full;
}

.form-group {
  @apply flex flex-col gap-y-7.5;
  @apply px-7.5 py-4;
  @apply rounded-4 border border-table-head-gray;
  @apply -mx-7.5;
}

.trash {
  @apply bg-red-100;
  @apply h-10 w-10;
  @apply p-2.5;
  @apply inline-block;
  @apply ml-2;
  border-radius: 100%;
}
</style>
